import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildTimeline, executionSpans, plsqlSpans, summarize, sqlBreakdown, timeComposition } from '../../utils/ashTimeline';
import TimeComposition, { COMMIT_COLOR } from './TimeComposition';
import { waitClassColor } from './waitClassColors';
import './AshTimeline.css';

/**
 * 선택한 세션의 **시간 축 막대**.
 *
 * recharts 를 쓰지 않고 SVG 를 직접 그리는 이유: 이 그림은 "한 줄에 수십 개의 짧은
 * 구간"이라 차트 라이브러리의 계열(series) 모델과 맞지 않는다. 계열로 만들면 구간마다
 * 계열이 하나씩 생겨 범례가 수십 줄이 된다.
 *
 * 규칙(계산은 `utils/ashTimeline.js`, 단위 테스트가 지킨다):
 *   - `event` 가 비면 ON CPU, 아니면 그 대기 이벤트.
 *   - **샘플이 없는 구간은 비워 둔다** — ASH 는 활성 세션만 찍으므로 빈 곳은 "비활성"이고,
 *     0 으로 메우면 "쉬지 않고 돌았다" 로 읽힌다.
 *   - 실행 경계는 `(sql_id, sql_exec_id)` 다. 같은 SQL 이라도 실행이 다르면 나눈다.
 *   - 병렬은 세션마다 줄을 따로 둔다(QC 가 맨 위).
 */

const LANE_H = 26;
const PLSQL_H = 13;      // PL/SQL 밴드 — 막대 위에 얇게 얹는다
const LANE_GAP = 10;
const TOP_PAD = 22;      // 실행 경계 라벨 자리
const BOTTOM_PAD = 18;   // 시간 눈금 자리
const LABEL_W = 132;

/** PL/SQL 구간 색 — SQL 막대(초록/파랑)와 섞이지 않는 보라 계열 */
const PLSQL_COLOR = '#8b5cf6';
const PLSQL_INNER_COLOR = '#c4b5fd';

const CPU_COLOR = '#16a34a';
const GAP_COLOR = 'transparent';

function fmtClock(ms) {
    const d = new Date(ms);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function fmtSec(ms) {
    return `${Math.round((ms / 1000) * 10) / 10}s`;
}

/** 확대의 하한 — 표본이 1초라 20초보다 좁히면 막대가 몇 개뿐이다 */
const MIN_VIEW_MS = 20 * 1000;
/** 실행 경계선끼리 이보다 가까우면 긋지 않는다 — 배치 세션은 실행 수백 번이 한 줄에 겹쳐 막대가 선으로 덮였다(2026-10-22 신고) */
const MIN_BOUNDARY_GAP_PX = 6;
/** SQL 별 합계 표의 줄 수 */
const SQL_ROWS = 15;
/** 오른쪽 「소요 시간 구성」 칸 — 이보다 좁으면 타임라인 아래로 내린다 */
const SIDE_W = 340;
const SIDE_MIN_BOX = 1000;

const AshTimeline = ({ samples, width: minWidth = 900, onSelect, isDarkMode = false, resetKey, sqlArea, onOpenSqlText, onOpenHistory, pool = false, commitStats = null, truncated = false }) => {
    const { t } = useTranslation();
    const [hovered, setHovered] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    // 보는 구간 — null 이면 전체. 끌어서 좁히고 「전체」 로 되돌린다(2026-10-22 — 14분짜리 배치가 한 줄에 눌려 읽을 수 없었다)
    const [view, setView] = useState(null);
    const [drag, setDrag] = useState(null);           // { x0, x1 } — svg 좌표
    const justDragged = useRef(false);
    // 표에서 고른 SQL — 막대에서 그 SQL 만 진하게
    const [focusSql, setFocusSql] = useState(null);
    // 폭은 칸을 따라간다(예전에는 980 고정이라 넓은 화면에서도 좁았다)
    const boxRef = useRef(null);
    const [boxW, setBoxW] = useState(0);
    useEffect(() => {
        const el = boxRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([e]) => { const w = Math.floor(e.contentRect.width); if (w > 0) setBoxW(w); });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    useEffect(() => { setView(null); setFocusSql(null); setSelectedKey(null); }, [resetKey]);

    const model = useMemo(() => buildTimeline(samples), [samples]);
    const { lanes, from: allFrom, to: allTo } = model;
    const from = view ? Math.max(view[0], allFrom) : allFrom;
    const to = view ? Math.min(view[1], allTo) : allTo;
    const breakdown = useMemo(() => (lanes.length && from != null ? sqlBreakdown(lanes, from, to) : []), [lanes, from, to]);
    const comp = useMemo(() => (lanes.length && from != null ? timeComposition(lanes, from, to, { pool }) : null), [lanes, from, to, pool]);
    // 커밋 막대의 빗금 무늬 — 한 화면에 타임라인이 여럿일 수 있어 id 를 나눈다
    const patId = `ash-commit-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

    if (!lanes.length || allFrom == null) {
        return <div className="ash-timeline-empty" ref={boxRef}>{t('monitoring:sa.tl.noSamples')}</div>;
    }

    // 넓으면 오른쪽에 「소요 시간 구성」 칸을 두고 그만큼 타임라인을 좁힌다
    const side = (boxW || minWidth) >= SIDE_MIN_BOX;
    const width = Math.max((boxW || minWidth) - (side ? SIDE_W + 16 : 0), 480);
    const fmtDur = (ms) => {
        const sec = Math.round(ms / 1000);
        if (sec >= 3600) return t('monitoring:sa.tc.durH', { h: Math.floor(sec / 3600), m: Math.floor((sec % 3600) / 60) });
        if (sec >= 60) return t('monitoring:sa.tl.spanMin', { m: Math.floor(sec / 60), s: sec % 60 });
        return t('monitoring:sa.tl.spanSec', { s: sec });
    };
    const plotW = Math.max(120, width - LABEL_W - 12);
    const span = Math.max(1, to - from);
    const x = (ms) => LABEL_W + ((ms - from) / span) * plotW;
    const msAt = (px) => from + ((Math.min(Math.max(px, LABEL_W), LABEL_W + plotW) - LABEL_W) / plotW) * span;
    const inView = (a, b) => b > from && a < to;
    // 보는 구간 밖으로 나가는 막대는 잘라 그린다
    const cx = (ms) => x(Math.min(Math.max(ms, from), to));
    // PL/SQL 이 하나도 없으면 밴드 자리를 비워 두지 않는다 — 빈 띠 두 줄이 그림을 늘일 뿐이다
    const hasPlsql = lanes.some(l => plsqlSpans(l, 'entry').length > 0);
    const bandH = hasPlsql ? PLSQL_H * 2 + 3 : 0;
    const rowH = LANE_H + bandH + LANE_GAP;
    const height = TOP_PAD + lanes.length * rowH + BOTTOM_PAD;
    const textColor = isDarkMode ? '#e5e7eb' : '#374151';
    const mutedColor = isDarkMode ? '#6b7280' : '#9ca3af';
    const gridColor = isDarkMode ? '#374151' : '#e5e7eb';

    // 시간 눈금 — 5칸이면 충분하다. 더 촘촘하면 글자가 겹친다.
    const tickN = Math.max(3, Math.min(10, Math.floor(plotW / 140)));
    const ticks = Array.from({ length: tickN + 1 }, (_, i) => from + (span * i) / tickN);

    // 끌어서 확대 — svg 좌표(화면 px 끼리의 비율이라 화면 확대와 무관하다)
    const svgX = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        return r.width > 0 ? ((e.clientX - r.left) / r.width) * width : 0;
    };
    const onDown = (e) => {
        if (e.button !== 0) return;
        const px = svgX(e);
        if (px < LABEL_W) return;
        setDrag({ x0: px, x1: px });
    };
    const onMove = (e) => { if (drag) setDrag({ ...drag, x1: svgX(e) }); };
    const onUp = () => {
        if (!drag) return;
        const lo = Math.min(drag.x0, drag.x1); const hi = Math.max(drag.x0, drag.x1);
        setDrag(null);
        if (hi - lo < 6) return;                  // 그냥 누른 것 — 막대 고르기로 둔다
        justDragged.current = true;
        setTimeout(() => { justDragged.current = false; }, 0);
        let f = msAt(lo); let tt = msAt(hi);
        if (tt - f < MIN_VIEW_MS) { const mid = (f + tt) / 2; f = mid - MIN_VIEW_MS / 2; tt = mid + MIN_VIEW_MS / 2; }
        setView([Math.max(allFrom, f), Math.min(allTo, tt)]);
    };
    // 두 배로 넓힌다 — 전체를 덮으면 「전체」 와 같다
    const zoomOut = () => {
        const mid = (from + to) / 2;
        const f = mid - span; const tt = mid + span;
        setView(f <= allFrom && tt >= allTo ? null : [Math.max(allFrom, f), Math.min(allTo, tt)]);
    };
    const fmtSpan = (ms) => (ms >= 60000
        ? t('monitoring:sa.tl.spanMin', { m: Math.floor(ms / 60000), s: Math.round((ms % 60000) / 1000) })
        : t('monitoring:sa.tl.spanSec', { s: Math.round(ms / 1000) }));

    const colorOf = (seg) => {
        if (seg.kind === 'gap') return GAP_COLOR;
        if (seg.kind === 'cpu') return CPU_COLOR;
        if (seg.kind === 'commit') return `url(#${patId})`;
        return waitClassColor(seg.waitClass, isDarkMode) || (isDarkMode ? '#60a5fa' : '#3b82f6');
    };

    const handleClick = (lane, seg) => {
        if (justDragged.current) return;
        const key = `${lane.key}:${seg.from}`;
        setSelectedKey(key);
        if (onSelect) onSelect({ lane, segment: seg });
    };

    return (
        <div className="ash-timeline" ref={boxRef}>
            <div className="ash-timeline-bar">
                <span className="ash-timeline-range">
                    {fmtClock(from)} ~ {fmtClock(to)} <em>({fmtSpan(to - from)})</em>
                </span>
                <span className="ash-timeline-hint">{t('monitoring:sa.tl.dragHint')}</span>
                {view && (
                    <>
                        <button type="button" className="ash-timeline-btn" onClick={zoomOut}>{t('monitoring:sa.tl.zoomOut')}</button>
                        <button type="button" className="ash-timeline-btn" onClick={() => setView(null)}>{t('monitoring:sa.tl.zoomAll')}</button>
                    </>
                )}
            </div>
            <div className={side ? 'ash-timeline-main side' : 'ash-timeline-main'}>
            <div className="ash-timeline-plot">
            <svg width={width} height={height} role="img" className={drag ? 'ash-timeline-dragging' : undefined}
                onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={() => setDrag(null)}>
                <defs>
                    {/* 커밋 — 빗금. SQL 의 대기(파랑)·CPU(초록)와 한눈에 갈린다 */}
                    <pattern id={patId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect width="6" height="6" fill={COMMIT_COLOR} />
                        <line x1="0" y1="0" x2="0" y2="6" stroke={isDarkMode ? '#78350f' : '#fff7ed'} strokeWidth="2.5" />
                    </pattern>
                </defs>
                {/* 시간 눈금 */}
                {ticks.map((ms, i) => (
                    <g key={i}>
                        <line x1={x(ms)} y1={TOP_PAD - 6} x2={x(ms)} y2={height - BOTTOM_PAD} stroke={gridColor} strokeDasharray="3 3" />
                        <text x={x(ms)} y={height - 5} textAnchor="middle" fontSize={10} fill={mutedColor}>{fmtClock(ms)}</text>
                    </g>
                ))}

                {lanes.map((lane, li) => {
                    const bandY = TOP_PAD + li * rowH;
                    const y = bandY + bandH;          // SQL 막대는 밴드 아래
                    const entrySpans = hasPlsql ? plsqlSpans(lane, 'entry') : [];
                    const innerSpans = hasPlsql ? plsqlSpans(lane, 'inner') : [];
                    const sum = summarize(lane);
                    const spans = executionSpans(lane);
                    return (
                        <g key={lane.key}>
                            {/* 줄 이름 — QC 인지 워커인지가 보여야 병렬 실행이 읽힌다 */}
                            <text x={0} y={y + LANE_H / 2 + 1} dominantBaseline="middle" fontSize={11} fill={textColor}>
                                {/* SERIAL# 을 함께 적는다 — 같은 SID 라도 다른 세션일 수 있고, 줄 이름이 같으면
                                    여러 줄이 왜 있는지 읽을 수 없다(실측: 「워커 · SID 1491」 7줄이 똑같이 보였다) */}
                                {lane.isQc
                                    ? t(pool ? 'monitoring:sa.tl.poolLane' : 'monitoring:sa.tl.qc', { sid: lane.sessionId, serial: lane.sessionSerial ?? '?' })
                                    /* 워커 이름·번호는 PROGRAM 에서 읽는 값이라 없을 수 있다 — 없으면 세션 번호만 적는다 */
                                    : lane.pxServerName
                                        ? t('monitoring:sa.tl.pxName', { name: lane.pxServerName, sid: lane.sessionId, serial: lane.sessionSerial ?? '?' })
                                        : (lane.pxServerNumber != null
                                            ? t('monitoring:sa.tl.px', { n: lane.pxServerNumber, sid: lane.sessionId, serial: lane.sessionSerial ?? '?' })
                                            : t('monitoring:sa.tl.pxNoNum', { sid: lane.sessionId, serial: lane.sessionSerial ?? '?' }))}
                            </text>
                            <text x={0} y={y + LANE_H / 2 + 13} dominantBaseline="middle" fontSize={9} fill={mutedColor}>
                                {`CPU ${fmtSec(sum.cpuMs)} · ${t('monitoring:sa.tl.wait')} ${fmtSec(sum.waitMs)}${sum.commitMs ? ` · ${t('monitoring:sa.tc.commit')} ${fmtSec(sum.commitMs)}` : ''}`}
                            </text>

                            {/* PL/SQL 밴드 — 위가 **최상위 호출**(프로시저 실행 구간), 아래가 **지금 실행 중인 서브프로그램**.
                                둘은 다른 질문에 답한다: "어느 프로시저 호출인가" vs "그 안 어디인가". */}
                            {hasPlsql && entrySpans.filter(sp => inView(sp.from, sp.to)).map((sp, i) => (
                                <g key={`pe-${i}`}>
                                    <rect
                                        x={cx(sp.from)} y={bandY}
                                        width={Math.max(1, cx(sp.to) - cx(sp.from))} height={PLSQL_H}
                                        fill={PLSQL_COLOR} rx={2}
                                        className="ash-timeline-seg"
                                        onMouseEnter={() => setHovered({ lane, plsql: sp })}
                                        onMouseLeave={() => setHovered(null)}
                                        onClick={() => onSelect && onSelect({ lane, plsqlSpan: sp })}
                                    />
                                    {cx(sp.to) - cx(sp.from) > 70 && (
                                        <text x={cx(sp.from) + 4} y={bandY + PLSQL_H - 3} fontSize={9} fill="#fff" pointerEvents="none">
                                            {sp.name || sp.id}
                                        </text>
                                    )}
                                </g>
                            ))}
                            {hasPlsql && innerSpans.filter(sp => inView(sp.from, sp.to)).map((sp, i) => (
                                <rect
                                    key={`pi-${i}`}
                                    x={cx(sp.from)} y={bandY + PLSQL_H + 2}
                                    width={Math.max(1, cx(sp.to) - cx(sp.from))} height={PLSQL_H - 2}
                                    fill={PLSQL_INNER_COLOR} rx={2}
                                    className="ash-timeline-seg"
                                    onMouseEnter={() => setHovered({ lane, plsql: sp, inner: true })}
                                    onMouseLeave={() => setHovered(null)}
                                    onClick={() => onSelect && onSelect({ lane, plsqlSpan: sp, inner: true })}
                                />
                            ))}

                            {/* 바탕 — 비활성 구간이 "빈 자리" 로 보이도록 옅게 깐다 */}
                            <rect x={LABEL_W} y={y} width={plotW} height={LANE_H} fill={isDarkMode ? '#1f2937' : '#f9fafb'} rx={3} />

                            {lane.segments.map((seg, si) => {
                                if (seg.kind === 'gap') return null;      // 비활성은 그리지 않는다 — 빈 자리가 곧 그 뜻이다
                                if (!inView(seg.from, seg.to)) return null;
                                const sx = cx(seg.from);
                                const sw = Math.max(1, cx(seg.to) - sx);
                                const dim = focusSql && seg.sqlId !== focusSql;
                                const key = `${lane.key}:${seg.from}`;
                                const active = selectedKey === key;
                                return (
                                    <rect
                                        key={si}
                                        x={sx}
                                        y={y + 2}
                                        width={sw}
                                        height={LANE_H - 4}
                                        fill={colorOf(seg)}
                                        opacity={dim ? 0.18 : undefined}
                                        stroke={active ? (isDarkMode ? '#fff' : '#111827') : 'none'}
                                        strokeWidth={active ? 1.5 : 0}
                                        className="ash-timeline-seg"
                                        onMouseEnter={() => setHovered({ lane, seg })}
                                        onMouseLeave={() => setHovered(null)}
                                        onClick={() => handleClick(lane, seg)}
                                    />
                                );
                            })}

                            {/* 실행 경계 — 세로선 + SQL_ID. 같은 SQL 이라도 실행이 다르면 따로 그린다. */}
                            {/* 실행 경계 — 세로선 + SQL_ID. 같은 SQL 이라도 실행이 다르면 따로 그린다.
                                촘촘하면 솎는다 — 앞서 그은 선과 MIN_BOUNDARY_GAP_PX 안이면 건너뛴다(구간을 좁히면 다 보인다) */}
                            {(() => {
                                let lastX = -Infinity;
                                return spans.filter(sp => sp.from >= from && sp.from <= to).map((sp, i) => {
                                    const px = x(sp.from);
                                    if (px - lastX < MIN_BOUNDARY_GAP_PX) return null;
                                    lastX = px;
                                    return (
                                        <g key={`x-${i}`}>
                                            <line x1={px} y1={bandY - 4} x2={px} y2={y + LANE_H + 2} stroke={textColor} strokeWidth={1} opacity={0.5} />
                                            {li === 0 && cx(sp.to) - px > 46 && (
                                                <text x={px + 3} y={TOP_PAD - 8} fontSize={9} fill={textColor} opacity={0.8}>
                                                    {sp.sqlId}
                                                </text>
                                            )}
                                        </g>
                                    );
                                });
                            })()}

                            {/* 트랜잭션 — 막대 **아래쪽**에 굵은 선으로. 샘플에 잡힌 처음~마지막일 뿐이라
                                시작/종료를 단정하지 않고 표시만 한다. */}
                            {lane.transactions.filter(tx => inView(tx.from, tx.to)).map((tx, i) => (
                                <g key={`tx-${i}`}>
                                    <line
                                        x1={cx(tx.from)} y1={y + LANE_H + 1}
                                        x2={cx(tx.to)} y2={y + LANE_H + 1}
                                        stroke="#dc2626" strokeWidth={2.5}
                                    />
                                    {tx.from >= from && <circle cx={x(tx.from)} cy={y + LANE_H + 1} r={2.5} fill="#dc2626" />}
                                    {tx.to <= to && <circle cx={x(tx.to)} cy={y + LANE_H + 1} r={2.5} fill="#dc2626" />}
                                </g>
                            ))}
                        </g>
                    );
                })}
                {drag && Math.abs(drag.x1 - drag.x0) >= 2 && (
                    <rect x={Math.max(LABEL_W, Math.min(drag.x0, drag.x1))} y={TOP_PAD - 6}
                        width={Math.abs(drag.x1 - drag.x0)} height={height - BOTTOM_PAD - TOP_PAD + 6}
                        fill={isDarkMode ? 'rgba(96,165,250,0.18)' : 'rgba(59,130,246,0.14)'} stroke="#3b82f6" strokeDasharray="3 2" pointerEvents="none" />
                )}
            </svg>
            </div>
            {comp && (
                <TimeComposition comp={comp} breakdown={breakdown} commitStats={commitStats} truncated={truncated}
                    focusSql={focusSql} onFocusSql={(id) => setFocusSql(v => (v === id ? null : id))} fmtDur={fmtDur} />
            )}
            </div>

            {hovered && (
                <div className="ash-timeline-tip">
                    {hovered.plsql ? (
                        <>
                            <b>{hovered.plsql.name || hovered.plsql.id}</b>
                            <span>{fmtSec(hovered.plsql.to - hovered.plsql.from)}</span>
                            <span>{t(hovered.inner ? 'monitoring:sa.tl.plsqlInner' : 'monitoring:sa.tl.plsqlEntry')}</span>
                        </>
                    ) : (
                        <>
                            <b>{hovered.seg.kind === 'cpu' ? t('monitoring:sa.tl.onCpu') : hovered.seg.kind === 'commit' ? `${t('monitoring:sa.tc.commit')}${hovered.seg.event ? ` · ${hovered.seg.event}` : ''}` : hovered.seg.event}</b>
                            <span>{fmtSec(hovered.seg.to - hovered.seg.from)}</span>
                            {hovered.seg.sqlId && <code>{hovered.seg.sqlId}</code>}
                        </>
                    )}
                </div>
            )}

            <div className="ash-timeline-legend">
                <span><i style={{ background: CPU_COLOR }} />{t('monitoring:sa.tl.onCpu')}</span>
                <span><i style={{ background: isDarkMode ? '#60a5fa' : '#3b82f6' }} />{t('monitoring:sa.tl.wait')}</span>
                <span><i className="ash-legend-commit" />{t('monitoring:sa.tc.commit')}</span>
                <span><i className="ash-legend-empty" />{t('monitoring:sa.tl.inactive')}</span>
                <span><i style={{ background: '#dc2626', height: 3 }} />{t('monitoring:sa.tl.transaction')}</span>
                {hasPlsql && <span><i style={{ background: PLSQL_COLOR }} />{t('monitoring:sa.tl.plsqlEntry')}</span>}
                {hasPlsql && <span><i style={{ background: PLSQL_INNER_COLOR }} />{t('monitoring:sa.tl.plsqlInner')}</span>}
            </div>

            {/* 보이는 구간의 SQL 별 합계(2026-10-22) — 배치처럼 실행이 수백 번이면 막대보다 이것이 답이다. 줄을 누르면 막대에서 그 SQL 만 진하게 */}
            {breakdown.length > 0 && (
                <div className="ash-sqlsum">
                    <div className="ash-sqlsum-head">
                        <b>{t('monitoring:sa.tl.bySql')}</b>
                        <span>{t('monitoring:sa.tl.bySqlHint')}</span>
                    </div>
                    <table className="ash-sqlsum-table">
                        <thead>
                            <tr>
                                <th>SQL_ID</th>
                                <th className="num">{t('monitoring:sa.tl.colTime')}</th>
                                <th className="num">{t('monitoring:sa.tl.colShare')}</th>
                                <th className="num">CPU</th>
                                <th className="num">{t('monitoring:sa.tl.wait')}</th>
                                <th>{t('monitoring:sa.tl.colTopEvent')}</th>
                                <th className="num" title={t('monitoring:sa.tl.colExecsHint')}>{t('monitoring:sa.tl.colExecs')}</th>
                                <th>SQL</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {breakdown.slice(0, SQL_ROWS).map(r => (
                                <tr key={r.sqlId} className={focusSql === r.sqlId ? 'on' : undefined}
                                    onClick={() => setFocusSql(v => (v === r.sqlId ? null : r.sqlId))}
                                    title={t('monitoring:sa.tl.bySqlRowHint')}>
                                    <td className="mono">{r.sqlId}</td>
                                    <td className="num">{fmtSec(r.ms)}</td>
                                    <td className="num share">
                                        <span className="ash-sqlsum-bar"><i style={{ width: `${Math.round(r.share * 100)}%` }} /></span>
                                        {Math.round(r.share * 1000) / 10}%
                                    </td>
                                    <td className="num">{fmtSec(r.cpuMs)}</td>
                                    <td className="num">{fmtSec(r.waitMs)}</td>
                                    <td className="ev" title={r.topEvent || ''}>{r.topEvent || '-'}</td>
                                    <td className="num">{r.execCount || '-'}</td>
                                    <td className="sql" title={(sqlArea && sqlArea[r.sqlId] && sqlArea[r.sqlId].sqlHead) || ''}>
                                        {(sqlArea && sqlArea[r.sqlId] && sqlArea[r.sqlId].sqlHead) || '-'}
                                    </td>
                                    <td className="acts" onClick={(e) => e.stopPropagation()}>
                                        {onOpenSqlText && <button type="button" className="ash-timeline-btn" onClick={() => onOpenSqlText(r.sqlId)}>{t('monitoring:sa.sqlFull')}</button>}
                                        {onOpenHistory && <button type="button" className="ash-timeline-btn" onClick={() => onOpenHistory(r.sqlId)}>{t('monitoring:sa.toHistory')}</button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {breakdown.length > SQL_ROWS && <div className="ash-sqlsum-more">{t('monitoring:sa.tl.bySqlMore', { n: breakdown.length - SQL_ROWS })}</div>}
                </div>
            )}
        </div>
    );
};

export default AshTimeline;
